import { useId } from "react"
import type { CoveredProceduresWidgetProps } from "@/types"
import { cn } from "@/utils/cn"

/** Covered procedures as a grouped Category -> Procedure table; mobile-friendly */
export function CoveredProceduresWidget({ data }: CoveredProceduresWidgetProps) {
  const categories = data.categories.filter((c) => c.procedures.length > 0)
  const headingId = useId()

  if (categories.length === 0) {
    return null
  }

  return (
    <section
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      aria-labelledby={headingId}
    >
      <h3
        id={headingId}
        className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-900"
      >
        Covered Procedures
      </h3>

      <div
        className="overflow-x-auto"
        role="region"
        aria-label="Covered procedures table"
        tabIndex={0}
      >
        <table
          aria-labelledby={headingId}
          className="w-full border-collapse text-left text-sm"
        >
          <thead>
            <tr className="bg-gray-50 text-gray-700">
              <th
                scope="col"
                className="w-1/3 border-b border-r border-gray-200 px-4 py-2 font-medium"
              >
                Category
              </th>
              <th scope="col" className="border-b border-gray-200 px-4 py-2 font-medium">
                Procedure
              </th>
            </tr>
          </thead>
          {categories.map((category, categoryIdx) => (
            <tbody key={category.name}>
              {category.procedures.map((procedure, procedureIdx) => {
                const isFirstInCategory = procedureIdx === 0
                // Strong line between category groups; light line between rows within one.
                const groupSeparator = isFirstInCategory && categoryIdx > 0
                return (
                  <tr key={`${category.name}-${procedureIdx}-${procedure}`}>
                    {isFirstInCategory && (
                      <th
                        scope="rowgroup"
                        rowSpan={category.procedures.length}
                        className={cn(
                          "border-r border-gray-200 px-4 py-2 align-top font-semibold text-gray-900 break-words",
                          categoryIdx > 0 && "border-t border-gray-200",
                        )}
                      >
                        {category.name}
                      </th>
                    )}
                    <td
                      className={cn(
                        "px-4 py-2 text-gray-700 break-words",
                        groupSeparator && "border-t border-gray-200",
                        !isFirstInCategory && "border-t border-gray-100",
                      )}
                    >
                      {procedure}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </table>
      </div>
    </section>
  )
}
